/**
 * localStorage persistence layer for user-defined custom weapons.
 *
 * Keys:
 *   frostgrave:custom-weapons   → Weapon[]  (full hydrated objects)
 */

import { Weapon } from '../types';

const KEY = 'frostgrave:custom-weapons';

function readAll(): Weapon[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as Weapon[]) : [];
  } catch {
    return [];
  }
}

function writeAll(weapons: Weapon[]): void {
  localStorage.setItem(KEY, JSON.stringify(weapons));
}

export const customWeaponService = {
  getAll(): Weapon[] {
    return readAll();
  },

  create(data: Omit<Weapon, 'id'>): Weapon {
    const weapon: Weapon = { ...data, id: crypto.randomUUID() };
    writeAll([...readAll(), weapon]);
    return weapon;
  },

  update(id: string, data: Partial<Omit<Weapon, 'id'>>): Weapon | null {
    const all = readAll();
    const idx = all.findIndex((w) => w.id === id);
    if (idx === -1) return null;
    const updated: Weapon = { ...all[idx], ...data, id };
    const next = [...all];
    next[idx] = updated;
    writeAll(next);
    return updated;
  },

  delete(id: string): void {
    writeAll(readAll().filter((w) => w.id !== id));
  },

  /** Overwrites the entire local list — used when applying a Drive download. */
  replaceAll(weapons: Weapon[]): void {
    writeAll(weapons);
  },
};
